/**
 * Accounting Services › Bills & Payables: the bill inbox (read automatically, checked, posted to
 * QuickBooks with the file attached), open bills with payments, and payables by age.
 */
import { formatCents } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, FileText, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { AccountSelect, MoneyInput, SignedMoney, useAccounts, type ListRow } from "@/components/bookkeeping";
import { askText } from "@/components/dialogs";
import { Empty, Loading, Segmented } from "@/components/page";
import { UploadButton } from "@/components/panels";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { formatDate, plural } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { useToast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

type BillLine = { description: string; amountCents: number; accountId: string | null; classId: string | null };
type Bill = {
  id: string;
  source: "staff" | "portal";
  status: "needs_review" | "posted" | "rejected";
  readNote: string | null;
  vendorQboId: string | null;
  vendorName: string;
  billNumber: string;
  billDate: string | null;
  dueDate: string | null;
  totalCents: number | null;
  lines: BillLine[];
  memo: string;
  postedAt: string | null;
  rejectedReason: string | null;
  createdAt: string;
  link: string | null;
  document: { id: string; filename: string; contentType: string } | null;
};
type OpenItem = { id: string; vendor?: string; customer?: string; docNumber: string; date: string; dueDate: string; total: number; balance: number; daysOverdue: number; link: string };
type Aging = { columns: string[]; rows: Array<{ name: string; amounts: Record<string, number> }> };
type Payables = { today: string; open: OpenItem[]; aging: Aging; payFrom: Array<{ qboId: string; name: string; type: string }> };

export const BILL_ACCOUNT_TYPES = ["Expense", "Cost of Goods Sold", "Other Expense", "Other Current Asset", "Fixed Asset", "Other Asset", "Other Current Liability"];
const field = "h-9 rounded-md border border-input bg-background px-2 text-sm";

/** Section switcher inside a page, so each part has the full width. */
export function SubTabs<T extends string>({ value, onChange, tabs, label = "Sections" }: { value: T; onChange: (v: T) => void; tabs: Array<[T, string, number?]>; label?: string }) {
  return (
    <div className="mb-4">
      <Segmented label={label} value={value} onChange={onChange} options={tabs.map(([k, l, n]) => ({ value: k, label: l, count: n || undefined }))} />
    </div>
  );
}

/** Payables or receivables by age, as QuickBooks reports them. */
export function AgingTable({ aging, who }: { aging: Aging; who: string }) {
  if (!aging.rows.length) return <Empty>Nothing outstanding.</Empty>;
  const cols = aging.columns;
  return (
    <TableScroll label={`${who} aging table`} className="rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-secondary/60 text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">{who}</th>
            {cols.map((c) => (
              <th key={c} className="whitespace-nowrap px-3 py-2 text-right font-medium">
                {c === "Total" ? "Total" : c === "Current" ? "Current" : `${c} days`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {aging.rows.map((r) => (
            <tr key={r.name}>
              <td className="px-3 py-2">{r.name}</td>
              {cols.map((c) => {
                const v = r.amounts[c] ?? 0;
                const late = c !== "Current" && c !== "Total" && v > 0;
                return (
                  <td key={c} className={cn("tabular whitespace-nowrap px-3 py-2 text-right", c === "Total" && "font-medium", late && "text-amber-700 dark:text-amber-300", v === 0 && "text-muted-foreground")}>
                    {v === 0 ? "—" : formatCents(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

/** Shows a PDF or image the firm stored, fetched as a file (downloads are never rendered by the server). */
function FilePreview({ doc }: { doc: { id: string; filename: string; contentType: string } }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const previewable = doc.contentType === "application/pdf" || doc.contentType === "image/png" || doc.contentType === "image/jpeg";
  useEffect(() => {
    if (!previewable) return;
    let gone = false;
    let made: string | null = null;
    fetch(`/api/documents/${doc.id}/download`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((b) => {
        if (gone) return;
        made = URL.createObjectURL(new Blob([b], { type: doc.contentType }));
        setUrl(made);
      })
      .catch(() => !gone && setFailed(true));
    return () => {
      gone = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [doc.id, doc.contentType, previewable]);
  if (!previewable || failed) {
    return (
      <a href={`/api/documents/${doc.id}/download`} className="flex h-full min-h-40 items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm text-primary hover:underline">
        <FileText className="h-4 w-4" /> Download {doc.filename}
      </a>
    );
  }
  if (!url) return <div className="flex h-full min-h-40 items-center justify-center text-sm text-muted-foreground">Loading the bill…</div>;
  return doc.contentType === "application/pdf" ? <iframe title={doc.filename} src={url} className="h-[60vh] w-full rounded-md border border-border" /> : <img src={url} alt={doc.filename} className="max-h-[60vh] w-full rounded-md border border-border object-contain" />;
}

export function BillsPayables({ clientId, me }: { clientId: string; me: Me }) {
  const { toast } = useToast();
  const canEdit = hasPermission(me, "engagements.write");
  const [tab, setTab] = useState<"inbox" | "open" | "aging">("inbox");
  const { data: bills, isLoading, error } = useQuery<Bill[]>({ queryKey: [`/api/clients/${clientId}/books/bills`], retry: false });
  const { data: pay, isLoading: payLoading, error: payError } = useQuery<Payables>({ queryKey: [`/api/clients/${clientId}/books/payables`], retry: false, enabled: tab !== "inbox" });
  const [open, setOpen] = useState<Bill | null>(null);
  const [paying, setPaying] = useState<OpenItem | null>(null);
  const upload = useMutation({
    meta: { inlineError: true },
    mutationFn: (f: File) => api.upload<Bill>(`/api/clients/${clientId}/books/bills`, {}, f),
    onSuccess: (b) => {
      setOpen(b);
      toast({ title: "Bill received", description: b.readNote ?? undefined });
    },
  });
  const waiting = (bills ?? []).filter((b) => b.status === "needs_review");
  const done = (bills ?? []).filter((b) => b.status !== "needs_review");
  return (
    <div>
      <SubTabs
        value={tab}
        onChange={setTab}
        tabs={[
          ["inbox", "Bill inbox", waiting.length],
          ["open", "Open bills"],
          ["aging", "Payables aging"],
        ]}
      />
      {tab === "inbox" &&
        (isLoading ? (
          <Loading />
        ) : error || !bills ? (
          <Empty>{errorMessage(error)}</Empty>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">Bills the client sends from the portal arrive here, read automatically. Check each one, then post it to QuickBooks with the bill attached.</p>
              {canEdit && <UploadButton label={upload.isPending ? "Reading the bill…" : "Upload a bill"} disabled={upload.isPending} onFile={(f) => upload.mutate(f)} />}
            </div>
            {upload.error && <p className="text-sm text-destructive">{errorMessage(upload.error)}</p>}
            {!waiting.length ? (
              <Empty>No bills are waiting for review.</Empty>
            ) : (
              <ul className="divide-y divide-border rounded-lg border border-border">
                {waiting.map((b) => (
                  <li key={b.id}>
                    <button type="button" onClick={() => setOpen(b)} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-3 py-3 text-left text-sm hover:bg-muted/50">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{b.vendorName || b.document?.filename || "Bill"}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {b.source === "portal" ? "Sent by the client" : "Uploaded by staff"} · {formatDate(b.createdAt)}
                          {b.billNumber && ` · #${b.billNumber}`}
                          {b.dueDate && ` · due ${formatDate(b.dueDate)}`}
                        </span>
                        {b.readNote && !b.readNote.startsWith("Read automatically") && (
                          <span className="mt-0.5 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300">
                            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {b.readNote}
                          </span>
                        )}
                      </span>
                      <span className="tabular font-medium">{b.totalCents === null ? "—" : formatCents(b.totalCents)}</span>
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">Review</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {done.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground">Posted and rejected ({done.length})</summary>
                <ul className="mt-2 divide-y divide-border rounded-lg border border-border">
                  {done.map((b) => (
                    <li key={b.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-xs">
                      <span className="w-24 shrink-0">{formatDate(b.createdAt)}</span>
                      <span className="min-w-0 flex-1 truncate">
                        {b.vendorName || b.document?.filename}
                        {b.billNumber && ` #${b.billNumber}`}
                      </span>
                      <span className="tabular">{b.totalCents === null ? "" : formatCents(b.totalCents)}</span>
                      {b.status === "posted" ? (
                        b.link && (
                          <a href={b.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                            Posted <ExternalLink className="h-3 w-3" />
                          </a>
                        )
                      ) : (
                        <span className="text-muted-foreground">Rejected: {b.rejectedReason}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        ))}
      {tab !== "inbox" &&
        (payLoading ? (
          <Loading />
        ) : payError || !pay ? (
          <Empty>{errorMessage(payError)}</Empty>
        ) : tab === "aging" ? (
          <AgingTable aging={pay.aging} who="Vendor" />
        ) : !pay.open.length ? (
          <Empty>No open bills in QuickBooks.</Empty>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {pay.open.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-sm">
                <span className="min-w-0 flex-1 basis-full sm:basis-0">
                  <span className="block truncate font-medium">{b.vendor}</span>
                  <span className="block text-xs text-muted-foreground">
                    {b.docNumber && `#${b.docNumber} · `}
                    {formatDate(b.date)} · due {formatDate(b.dueDate)}
                  </span>
                </span>
                {b.daysOverdue > 0 && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-900 dark:bg-red-900/30 dark:text-red-200">{plural(b.daysOverdue, "day")} past due</span>}
                <span className="tabular text-right">
                  <span className="block font-medium">{formatCents(b.balance)}</span>
                  {b.balance !== b.total && <span className="block text-xs text-muted-foreground">of {formatCents(b.total)}</span>}
                </span>
                <a href={b.link} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline" aria-label="Open in QuickBooks">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => setPaying(b)}>
                    Record payment
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ))}
      {open && <BillDialog clientId={clientId} me={me} bill={open} onClose={() => setOpen(null)} />}
      {paying && pay && <PaymentDialog kind="bill" clientId={clientId} item={paying} today={pay.today} accounts={pay.payFrom} onClose={() => setPaying(null)} />}
    </div>
  );
}

function BillDialog({ clientId, me, bill, onClose }: { clientId: string; me: Me; bill: Bill; onClose: () => void }) {
  const { toast } = useToast();
  const canEdit = hasPermission(me, "engagements.write");
  const accounts = useAccounts(clientId).filter((a) => BILL_ACCOUNT_TYPES.includes(a.accountType) && !/Undeposited/i.test(a.fullyQualifiedName));
  const { data: vendors } = useQuery<ListRow[]>({ queryKey: [`/api/clients/${clientId}/books/lists/vendors`], staleTime: 5 * 60_000, retry: false });
  const { data: classes } = useQuery<ListRow[]>({ queryKey: [`/api/clients/${clientId}/books/lists/classes`], staleTime: 5 * 60_000, retry: false });
  const [b, setB] = useState(bill);
  const [f, setF] = useState({ vendorQboId: bill.vendorQboId, vendorName: bill.vendorName, billNumber: bill.billNumber, billDate: bill.billDate, dueDate: bill.dueDate, memo: bill.memo, lines: bill.lines.length ? bill.lines : [{ description: "", amountCents: 0, accountId: null, classId: null }] });
  const activeVendors = (vendors ?? []).filter((v) => v.active);
  const activeClasses = (classes ?? []).filter((c) => c.active);
  const total = f.lines.reduce((a, l) => a + l.amountCents, 0);
  const setLine = (i: number, patch: Partial<BillLine>) => setF({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const save = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send<Bill>("PATCH", `/api/bills/${b.id}`, { ...f, lines: f.lines.filter((l) => l.amountCents !== 0) }), onSuccess: (r) => setB({ ...b, ...r }) });
  const post = useMutation({
    meta: { inlineError: true },
    mutationFn: async () => {
      await save.mutateAsync();
      return api.send<Bill & { attachNote: string | null }>("POST", `/api/bills/${b.id}/post`, {});
    },
    onSuccess: (r) => {
      toast({ title: "Posted to QuickBooks", description: r.attachNote ?? "The bill is attached in QuickBooks." });
      onClose();
    },
  });
  const reject = useMutation({ meta: { inlineError: true }, mutationFn: (reason: string) => api.send("POST", `/api/bills/${b.id}/reject`, { reason }), onSuccess: onClose });
  const reread = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<Bill>("POST", `/api/bills/${b.id}/read`, {}),
    onSuccess: (r) => {
      setB({ ...b, ...r });
      setF({ vendorQboId: r.vendorQboId, vendorName: r.vendorName, billNumber: r.billNumber, billDate: r.billDate, dueDate: r.dueDate, memo: r.memo, lines: r.lines.length ? r.lines : f.lines });
    },
  });
  const ro = !canEdit || b.status !== "needs_review";
  const ready = (!!f.vendorQboId || !!f.vendorName.trim()) && !!f.billDate && f.lines.length > 0 && f.lines.every((l) => l.accountId && l.amountCents !== 0) && total > 0;
  const err = save.error ?? post.error ?? reject.error ?? reread.error;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review bill{b.vendorName ? ` · ${b.vendorName}` : ""}</DialogTitle>
          <DialogDescription>{b.readNote ?? "Check the details against the bill, then post it to QuickBooks."}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
          <div className="min-w-0 space-y-1">
            {b.document ? (
              <>
                <FilePreview doc={b.document} />
                <a href={`/api/documents/${b.document.id}/download`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                  <FileText className="h-3 w-3" /> {b.document.filename}
                </a>
              </>
            ) : (
              <Empty>The bill's file was deleted.</Empty>
            )}
          </div>
          <div className="min-w-0 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs text-muted-foreground sm:col-span-2">
                <span className="mb-1 block">Vendor</span>
                <select
                  className={cn(field, "w-full")}
                  disabled={ro}
                  value={f.vendorQboId ?? "new"}
                  onChange={(e) => {
                    const v = activeVendors.find((x) => x.id === e.target.value);
                    setF({ ...f, vendorQboId: v ? v.id : null, vendorName: v ? v.name : f.vendorName });
                  }}
                >
                  <option value="new">{f.vendorName ? `New vendor: ${f.vendorName}` : "Choose a vendor"}</option>
                  {activeVendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </label>
              {!f.vendorQboId && (
                <label className="text-xs text-muted-foreground sm:col-span-2">
                  <span className="mb-1 block">New vendor's name (added to QuickBooks when the bill is posted)</span>
                  <Input disabled={ro} value={f.vendorName} maxLength={100} onChange={(e) => setF({ ...f, vendorName: e.target.value })} />
                </label>
              )}
              <label className="text-xs text-muted-foreground">
                <span className="mb-1 block">Bill number</span>
                <Input disabled={ro} value={f.billNumber} maxLength={21} onChange={(e) => setF({ ...f, billNumber: e.target.value })} />
              </label>
              <label className="text-xs text-muted-foreground">
                <span className="mb-1 block">Bill date</span>
                <input type="date" disabled={ro} className={cn(field, "w-full")} value={f.billDate ?? ""} onChange={(e) => setF({ ...f, billDate: e.target.value || null })} />
              </label>
              <label className="text-xs text-muted-foreground">
                <span className="mb-1 block">Due date</span>
                <input type="date" disabled={ro} className={cn(field, "w-full")} value={f.dueDate ?? ""} onChange={(e) => setF({ ...f, dueDate: e.target.value || null })} />
              </label>
              <label className="text-xs text-muted-foreground">
                <span className="mb-1 block">Memo (in QuickBooks)</span>
                <Input disabled={ro} value={f.memo} maxLength={1000} onChange={(e) => setF({ ...f, memo: e.target.value })} />
              </label>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Lines</p>
              {f.lines.map((l, i) => (
                <div key={i} className="space-y-2 rounded-md border border-border p-2">
                  <div className="flex gap-2">
                    <Input aria-label={`Description, line ${i + 1}`} disabled={ro} placeholder="Description" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} />
                    <div className="w-40 shrink-0">
                      <SignedMoney label={`Amount, line ${i + 1}`} disabled={ro} value={l.amountCents} onChange={(c) => setLine(i, { amountCents: c })} />
                    </div>
                    {!ro && (
                      <button type="button" aria-label={`Remove line ${i + 1}`} disabled={f.lines.length <= 1} onClick={() => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive disabled:opacity-30">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <div className="min-w-0 flex-1">
                      <AccountSelect accounts={accounts} value={l.accountId ?? ""} onChange={(v) => setLine(i, { accountId: v || null })} label={`Account, line ${i + 1}`} disabled={ro} />
                    </div>
                    {activeClasses.length > 0 && (
                      <select aria-label={`Class, line ${i + 1}`} disabled={ro} className={cn(field, "w-32 shrink-0")} value={l.classId ?? ""} onChange={(e) => setLine(i, { classId: e.target.value || null })}>
                        <option value="">No class</option>
                        {activeClasses.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between text-sm">
                {!ro ? (
                  <Button size="sm" variant="ghost" onClick={() => setF({ ...f, lines: [...f.lines, { description: "", amountCents: 0, accountId: null, classId: null }] })}>
                    <Plus className="h-3.5 w-3.5" /> Add line
                  </Button>
                ) : (
                  <span />
                )}
                <span className="tabular font-semibold">Total {formatCents(total)}</span>
              </div>
            </div>
          </div>
        </div>
        {err && <p className="text-sm text-destructive">{errorMessage(err)}</p>}
        {!ro && (
          <DialogFooter className="flex-wrap gap-2">
            <Button
              variant="ghost"
              onClick={async () => {
                const reason = await askText({ title: "Reject this bill?", description: "It isn't posted. The reason is kept with the bill (for example, a duplicate or not the client's).", label: "Reason", required: true, confirmLabel: "Reject bill" });
                if (reason) reject.mutate(reason);
              }}
            >
              Reject
            </Button>
            <Button variant="ghost" disabled={reread.isPending} onClick={() => reread.mutate()}>
              {reread.isPending ? "Reading…" : "Read again"}
            </Button>
            <Button variant="outline" disabled={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
            <Button disabled={!ready || post.isPending} onClick={() => post.mutate()} title={ready ? undefined : "Choose the vendor and bill date, and give each line an account and amount"}>
              {post.isPending ? "Posting…" : "Approve and post to QuickBooks"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Pays an open bill, or records a customer's payment of an invoice. */
export function PaymentDialog({ kind, clientId, item, today, accounts, onClose }: { kind: "bill" | "invoice"; clientId: string; item: OpenItem; today: string; accounts: Array<{ qboId: string; name: string; type: string }>; onClose: () => void }) {
  const { toast } = useToast();
  const [amount, setAmount] = useState(item.balance);
  const [date, setDate] = useState(today);
  const [account, setAccount] = useState(accounts[0]?.qboId ?? "");
  const [ref, setRef] = useState("");
  const [key] = useState(() => crypto.randomUUID());
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      kind === "bill"
        ? api.send("POST", `/api/clients/${clientId}/books/bill-payments`, { billId: item.id, amountCents: amount, date, accountId: account, ...(ref ? { checkNumber: ref } : {}) }, key)
        : api.send("POST", `/api/clients/${clientId}/books/payments`, { invoiceId: item.id, amountCents: amount, date, depositAccountId: account, ...(ref ? { reference: ref } : {}) }, key),
    onSuccess: () => {
      toast({ title: "Payment recorded in QuickBooks" });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{kind === "bill" ? `Pay ${item.vendor}` : `Payment from ${item.customer}`}</DialogTitle>
          <DialogDescription>
            {kind === "bill" ? "Bill" : "Invoice"} {item.docNumber && `#${item.docNumber} `}· open balance {formatCents(item.balance)}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block">Amount</span>
            <MoneyInput label="Amount" value={amount} onChange={setAmount} />
          </label>
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block">Date</span>
            <input type="date" className={cn(field, "w-full")} value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
          </label>
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block">{kind === "bill" ? "Paid from" : "Deposited to"}</span>
            <select className={cn(field, "w-full")} value={account} onChange={(e) => setAccount(e.target.value)}>
              {accounts.map((a) => (
                <option key={a.qboId} value={a.qboId}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block">{kind === "bill" ? "Check number (optional)" : "Reference, such as a check number (optional)"}</span>
            <Input value={ref} maxLength={21} onChange={(e) => setRef(e.target.value)} />
          </label>
        </div>
        {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!amount || amount > item.balance || !account || save.isPending} onClick={() => save.mutate()}>
            Record {formatCents(amount)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
